import { useMemo, useState } from "react";
import { connect } from "react-redux";

import { ConfirmationDialog } from "components/Dialog/ConfirmationDialog";
import { DropDownList } from "components/DropDownList/DropDownList";
import { Panel } from "components/Panel/Panel";
import { Toggle } from "components/Toggle/Toggle";
import { Tooltip } from "components/Tooltip/Tooltip";
import { ClientMode } from "models/system";

import { setGeneralPreferenceAction } from "../actions/generalPreferenceAction";

import StorePatientEncounterDataTooltip from "./StorePatientEncounterDataTooltip";

import type { UserState } from "models/user";
import type { State } from "reducers";
import type { GeneralPreferenceObject } from "../models/systemPreferences";
import type { SystemPreferencesState } from "../models/systemPreferencesState";

import "../SystemPreferences.scss";

export const STORAGE_TYPE = {
  InMemory: "InMemory",
  Sql: "Sql",
} as const;

const SESSION_STORAGE_OPTIONS = [
  { id: STORAGE_TYPE.InMemory, title: "In-Memory Cache" as const },
  { id: STORAGE_TYPE.Sql, title: "Azure SQL Database" as const },
];

interface SecuritySettingsProps {
  setGeneralPreference: (
    preferenceName: "SessionStorageType" | "AllowUserDefaultRole",
    value: (typeof STORAGE_TYPE)[keyof typeof STORAGE_TYPE] | boolean,
    allGeneralPreferences: GeneralPreferenceObject,
  ) => void;
  systemPreferences: SystemPreferencesState;
  user: UserState;
}

const SecuritySettingsPure = ({
  setGeneralPreference,
  systemPreferences,
  user,
}: SecuritySettingsProps) => {
  const defaultStorageType = useMemo(
    () =>
      SESSION_STORAGE_OPTIONS.find(
        (item) => item.id === systemPreferences.generalPreferences.SessionStorageType,
      ) || SESSION_STORAGE_OPTIONS[1],
    [systemPreferences.generalPreferences.SessionStorageType],
  );
  const [selectedValue, setSelectedValue] = useState(defaultStorageType);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const onChangeStorePatient = (_, value: (typeof SESSION_STORAGE_OPTIONS)[number]) => {
    setSelectedValue(value);
    setIsOpenDialog(true);
  };

  const onCompleteChangeStorePatient = () => {
    setGeneralPreference(
      "SessionStorageType",
      selectedValue.id,
      systemPreferences.generalPreferences,
    );
    setIsOpenDialog(false);
  };

  const onCancelChangeStorePatient = () => {
    setSelectedValue(defaultStorageType);
    setIsOpenDialog(false);
  };

  const opposite = SESSION_STORAGE_OPTIONS.find((option) => option.id !== selectedValue.id);

  const dialogMessage = `Changing to ${selectedValue.title} will immediately remove all Patient Encounters from the ${opposite?.title}. Users will lose any Patient Encounters they are currently working on. Do you wish to proceed?`;

  const isAdminInInterfaceMode = user.clientMode === ClientMode.Interface;

  const allowUserDefaultRole = systemPreferences.generalPreferences.AllowUserDefaultRole;

  const onChangeAllowUserDefaultRole = () => {
    setGeneralPreference(
      "AllowUserDefaultRole",
      !allowUserDefaultRole,
      systemPreferences.generalPreferences,
    );
  };

  return (
    <>
      <Panel
        className="settings-panel security-settings-panel"
        title="Security Settings"
        testId="security-settings-panel"
      >
        <div className="control-row" data-testid="security-store-patient-container">
          <div className="security-settings-label">Store Patient Encounter Data</div>
          <Tooltip
            anchorElement="target"
            className="system-settings-tooltip"
            content={() => <StorePatientEncounterDataTooltip />}
            openDelay={300}
            position="right"
          >
            <i
              className="fas fa-question-circle security-settings-tooltip-icon"
              title="Store Patient Encounter Data"
            />
          </Tooltip>
          <DropDownList
            data={SESSION_STORAGE_OPTIONS}
            dataItemKey="id"
            disabled={!isAdminInInterfaceMode}
            onChange={onChangeStorePatient}
            returnFullRecord
            textField="title"
            value={selectedValue}
            dataTestId="security-store-patient"
          />
        </div>
        <div className="control-row">
          <div className="security-settings-label">Allow User Default Role</div>
          <Toggle
            testId="allow-user-default-role-toggle"
            checked={allowUserDefaultRole}
            onChange={onChangeAllowUserDefaultRole}
          />
        </div>
      </Panel>
      <ConfirmationDialog
        cancelText="Cancel"
        confirmText="Acknowledge"
        dialogMessage={dialogMessage}
        onConfirmClick={onCompleteChangeStorePatient}
        title="Confirmation"
        toggleDialog={onCancelChangeStorePatient}
        visible={isOpenDialog}
        testId="security-confirm-change-store-patient"
      />
    </>
  );
};

const mapStateToProps = (state: State) => ({
  systemPreferences: state.systemPreferences,
  user: state.user,
});

const mapDispatchToProps = {
  setGeneralPreference: setGeneralPreferenceAction,
};

export const SecuritySettings = connect(mapStateToProps, mapDispatchToProps)(SecuritySettingsPure);
